/**
 * rock_material.ts — the geology layer's ROCK surface (2026-09-25, visual-realism R3). TSL, no textures.
 *
 * One MeshStandardNodeMaterial per biome palette (the rock of the rocky highlands is grey granite,
 * the plains a tan sandstone, the desert an ochre sandstone, the crystal forest a blue-grey slate
 * with glints, the wetland a dark basalt, the fungal basin a purple-grey tuff):
 *   · STRATA: bedding bands by ABSOLUTE height (scene children carry absolute Y — the rebase is XZ
 *     only), 5–9 m apart and wandering, so a cliff, the outcrop beside it and the boulders below
 *     share one geology instead of each wearing its own stripes. The ±1.5 m WANDER is read at
 *     ABSOLUTE XZ too (gaps wave round 2, 2026-09-28): it read render-space `positionWorld.xz`,
 *     which the FP64 rebase moves every 10 km — every band on every rock jumped at each rebase.
 *     Now render XZ + the render anchor taken modulo the noise's own period on the CPU (in
 *     doubles), over a PERIODIC value noise (`rockWanderAt`, the CPU mirror) — the grime / ground
 *     origin-mod pattern; a 900 km coordinate never reaches the shader;
 *   · mottling and crevice shade from 3D noise in GEOMETRY space (the pattern rides the rock, not the
 *     world — MEASURE positionGeometry);
 *   · lichen / moss on the up-facing surfaces (normalWorld.y), stronger on the wetland palette;
 *   · a procedural BUMP (Mikkelsen's surface-gradient perturbation of two noise octaves, the height
 *     differentiated in screen space) — no normal map, no sampler;
 *   · roughness from the mottling (moss is rougher, fresh fracture faces smoother).
 * No positionNode: the instancer pins its motion vectors (pinStaticMotionVectors) — the static law.
 */
import * as THREE from 'three/webgpu';
import { Fn, float, vec3, mix, smoothstep, clamp, positionGeometry, positionWorld, normalWorld, normalView, positionView, mx_noise_float, fract, dFdx, dFdy, dot, abs, faceDirection, uniform, floor, normalGeometry, select, max } from 'three/tsl';
import { resolveSurfaceGradient, surfgradScreenHeight } from 'enginex/render/materials/SurfaceGradient.js';
import { applyEONDiffuse } from 'enginex/render/materials/EONDiffuse.js';
import { applyFlakeGlints } from 'enginex/render/materials/FlakeGlints.js';
import { EON_ROCK_ROUGHNESS, SLATE_FLAKES, eonDiffuseOn, flakeGlintsOn, materialModelStats } from '../material_models.js';
import type { Biome } from '@shared/terrain.js';
import { renderAnchor, onRenderAnchor } from '../local_origin.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface RockPalette { base: [number, number, number]; band: [number, number, number]; dark: [number, number, number]; moss: [number, number, number]; moss_amt: number; glint: number }
const s = (r: number, g: number, b: number): [number, number, number] => [Math.pow(r / 255, 2.2), Math.pow(g / 255, 2.2), Math.pow(b / 255, 2.2)];
export const ROCK_PALETTE: Record<Biome, RockPalette> = {
  rocky_highlands: { base: s(128, 126, 122), band: s(104, 100, 96), dark: s(58, 56, 54), moss: s(98, 108, 72), moss_amt: 0.45, glint: 0 },
  regolith_plains: { base: s(150, 132, 108), band: s(126, 106, 84), dark: s(70, 60, 50), moss: s(110, 112, 70), moss_amt: 0.3, glint: 0 },
  dune_desert: { base: s(176, 136, 96), band: s(150, 108, 74), dark: s(88, 62, 44), moss: s(150, 128, 90), moss_amt: 0.05, glint: 0 },
  crystal_forest: { base: s(112, 122, 134), band: s(88, 98, 114), dark: s(48, 54, 66), moss: s(80, 120, 118), moss_amt: 0.35, glint: 0.6 },
  moss_wetland: { base: s(84, 86, 84), band: s(68, 70, 70), dark: s(36, 38, 38), moss: s(62, 96, 58), moss_amt: 0.85, glint: 0 },
  fungal_basin: { base: s(112, 100, 112), band: s(92, 80, 96), dark: s(52, 44, 56), moss: s(110, 88, 118), moss_amt: 0.5, glint: 0 },
};

// ─── THE STRATA WANDER, AT ABSOLUTE XZ (gaps wave round 2, 2026-09-28) ─────────────────────────────────────────────
/** The wander lattice's pitch (m) — the old noise's 1/0.03 ≈ 33 m, rounded to a power of two. */
export const ROCK_WANDER_CELL_M = 32;
/** Lattice cells per period: the noise repeats every ROCK_WANDER_PERIOD_M, so the origin modulo that wraps seamlessly. */
export const ROCK_WANDER_CELLS = 128;
export const ROCK_WANDER_PERIOD_M = ROCK_WANDER_CELL_M * ROCK_WANDER_CELLS; // 4096 m
/** ± how far the bands wander (m). */
export const ROCK_WANDER_AMP_M = 1.5;

/** The render anchor modulo the period — added to render-space XZ in the shader (written on every rebase). */
export const uRockOriginMod = uniform(new THREE.Vector2(0, 0));
/** Anchor (absolute metres, integer, doubles) → the modulo the shader adds. */
export function rockOriginMod(ox: number, oz: number): [number, number] {
  const P = ROCK_WANDER_PERIOD_M;
  return [((ox % P) + P) % P, ((oz % P) + P) % P];
}
export function setRockOrigin(ox: number, oz: number): void {
  const [mx, mz] = rockOriginMod(ox, oz);
  (uRockOriginMod as unknown as { value: THREE.Vector2 }).value.set(mx, mz);
}
{ const a = renderAnchor(); setRockOrigin(a.x, a.z); onRenderAnchor(setRockOrigin); }

/** Dave Hoskins' hash12 over integer lattice coordinates already wrapped into [0, CELLS) — the CPU mirror of `hashNode`. */
function hashCpu(ix: number, iz: number): number {
  const fr = (v: number): number => v - Math.floor(v);
  let x = fr(ix * 0.1031), y = fr(iz * 0.1031), z = fr(ix * 0.1031);
  const d = x * (y + 33.33) + y * (z + 33.33) + z * (x + 33.33);
  x += d; y += d; z += d;
  return fr((x + y) * z);
}
const wrapCell = (i: number): number => i - ROCK_WANDER_CELLS * Math.floor(i / ROCK_WANDER_CELLS);
const quintic = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
/**
 * The band wander at ABSOLUTE world XZ (m) — the CPU mirror of the shader's periodic value noise (quintic-smoothed,
 * wrapped lattice), in [−AMP, +AMP]. Periodic: f(x + PERIOD, z) = f(x, z) = f(x, z + PERIOD).
 */
export function rockWanderAt(absX: number, absZ: number): number {
  const qx = absX / ROCK_WANDER_CELL_M, qz = absZ / ROCK_WANDER_CELL_M;
  const ix = Math.floor(qx), iz = Math.floor(qz);
  const ux = quintic(qx - ix), uz = quintic(qz - iz);
  const x0 = wrapCell(ix), x1 = wrapCell(ix + 1), z0 = wrapCell(iz), z1 = wrapCell(iz + 1);
  const a = hashCpu(x0, z0) + (hashCpu(x1, z0) - hashCpu(x0, z0)) * ux;
  const b = hashCpu(x0, z1) + (hashCpu(x1, z1) - hashCpu(x0, z1)) * ux;
  return ((a + (b - a) * uz) * 2 - 1) * ROCK_WANDER_AMP_M;
}
/** What the SHADER evaluates: render-space XZ + the anchor's modulo (the frame the GPU sees). */
export function rockWanderShaderMirror(renderX: number, renderZ: number, anchorX: number, anchorZ: number): number {
  const [mx, mz] = rockOriginMod(anchorX, anchorZ);
  return rockWanderAt(renderX + mx, renderZ + mz);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hashNode(ix: any, iz: any): N {
  const p3: N = fract(vec3(ix, iz, ix).mul(0.1031));
  const d: N = dot(p3, p3.yzx.add(33.33));
  const q: N = p3.add(d);
  return fract(q.x.add(q.y).mul(q.z));
}
/** The periodic wander node (m): render XZ + `uRockOriginMod`, the same arithmetic as `rockWanderAt`. */
function wanderNode(): N {
  const cells = float(ROCK_WANDER_CELLS);
  const wrap = (i: N): N => i.sub(floor(i.div(cells)).mul(cells)); // GLSL / WGSL `mod` differ on negatives — spell it out
  const q: N = positionWorld.xz.add(uRockOriginMod).div(ROCK_WANDER_CELL_M);
  const i0: N = floor(q);
  const f: N = q.sub(i0);
  const u: N = f.mul(f).mul(f).mul(f.mul(f.mul(6).sub(15)).add(10));
  const x0 = wrap(i0.x), x1 = wrap(i0.x.add(1)), z0 = wrap(i0.y), z1 = wrap(i0.y.add(1));
  const a: N = mix(hashNode(x0, z0), hashNode(x1, z0), u.x);
  const b: N = mix(hashNode(x0, z1), hashNode(x1, z1), u.x);
  return mix(a, b, u.y).mul(2).sub(1).mul(ROCK_WANDER_AMP_M);
}

const cache = new Map<Biome, THREE.MeshStandardNodeMaterial>();
/** The rock material for a biome (cached for the session — shared by every rock batch in that biome). */
export function rockMaterial(biome: Biome): THREE.MeshStandardNodeMaterial {
  const hit = cache.get(biome); if (hit) return hit;
  const m = buildRockMaterial(biome, { eon: eonDiffuseOn(), flakes: flakeGlintsOn() }); // material models R4: the flags at build
  cache.set(biome, m);
  return m;
}
/**
 * Build one rock material (uncached — the material bed builds each variant). `models` (shader gaps R4, material models):
 * `eon` = the EON rough diffuse where the palette has a diffuse roughness (EON_ROCK_ROUGHNESS); `flakes` = the slate's
 * footprint-filtered mica flakes INSTEAD of its old emissive glint (off: the old glint, byte for byte).
 */
export function buildRockMaterial(biome: Biome, models: { eon: boolean; flakes: boolean } = { eon: false, flakes: false }): THREE.MeshStandardNodeMaterial {
  const p = ROCK_PALETTE[biome];
  const m = new THREE.MeshStandardNodeMaterial();
  m.name = `geology-rock-${biome}`;
  const v3 = (c: [number, number, number]): N => vec3(c[0], c[1], c[2]);
  const pg = positionGeometry;
  const mott = mx_noise_float(pg.mul(1.7), 1, 0).mul(0.5).add(0.5);               // 0..1 mottling
  const fine = mx_noise_float(pg.mul(6.3).add(vec3(3.1, 7.7, 1.3)), 1, 0).mul(0.5).add(0.5);
  const cav = smoothstep(float(0.62), float(0.9), mx_noise_float(pg.mul(0.9).add(vec3(11.3, 0, 5.1)), 1, 0).mul(0.5).add(0.5));
  // bedding by absolute height: bands 5–9 m apart that wander ±1.5 m across the landscape — the wander at ABSOLUTE XZ
  // (gaps wave round 2: `positionWorld.xz` alone is render space, and every band jumped at each 10 km rebase)
  const wy = positionWorld.y.add(wanderNode());
  const band = smoothstep(float(0.35), float(0.65), abs(fract(wy.div(6.8)).sub(0.5)).mul(2));
  const color = Fn(() => {
    let c: N = mix(v3(p.base), v3(p.band), band.mul(0.7));
    c = c.mul(float(0.78).add(mott.mul(0.34))).mul(float(0.9).add(fine.mul(0.2)));
    c = mix(c, v3(p.dark), cav.mul(0.55));
    const up = smoothstep(float(0.5), float(0.88), normalWorld.y);
    const mossMask = up.mul(smoothstep(float(0.35), float(0.7), mott)).mul(p.moss_amt);
    c = mix(c, v3(p.moss), clamp(mossMask, 0, 0.9));
    return c;
  })();
  m.colorNode = color;
  m.roughnessNode = clamp(float(0.74).add(mott.mul(0.14)).add(cav.mul(0.08)), 0.55, 0.98);
  m.metalnessNode = float(0);
  const flakes = models.flakes && p.glint > 0;
  if (p.glint > 0 && !flakes) m.emissiveNode = vec3(0.35, 0.55, 0.7).mul(smoothstep(float(0.93), float(0.99), fine).mul(p.glint * 0.12));
  // procedural bump: height from two noise octaves in geometry space, Mikkelsen's surface gradient — THE NORMAL
  // CONTRACT (material models R4): the screen-height surface gradient (Mikkelsen 2020 Listing 6) × 0.15 m per unit
  // height, resolved once — the same direction as the 2010 form normalize(|det|·n − 0.15·sign(det)·grad) it replaces
  // (lib/normal_contract.ts rockBump*Cpu pins the two), and a degenerate det now returns n instead of normalize(0)
  m.normalNode = Fn(() => {
    const h = mx_noise_float(pg.mul(3.1), 1, 0).mul(0.55).add(mx_noise_float(pg.mul(11.7).add(vec3(5.2, 1.1, 9.4)), 1, 0).mul(0.22)).mul(0.9);
    const dHx = dFdx(h), dHy = dFdy(h);
    const sx = dFdx(positionView), sy = dFdy(positionView);
    const n = normalView;
    return resolveSurfaceGradient(n, surfgradScreenHeight(n, sx, sy, dHx, dHy, faceDirection).mul(0.15));
  })();
  materialModelStats.routedRock++;
  // THE EON DIFFUSE (material models R4): the rough palettes' diffuse is energy-preserving Oren–Nayar (a flatter,
  // back-scattering sunlit face — the regolith's grazing-sun look); the albedo is kept exactly ('white' mode)
  const eonR = EON_ROCK_ROUGHNESS[biome] ?? 0;
  if (models.eon && eonR > 0) { applyEONDiffuse(m, { roughness: eonR }); materialModelStats.eonRocks++; }
  // THE SLATE'S MICA (material models R4): footprint-filtered flakes in GEOMETRY space (they ride the rock; a rebase
  // changes nothing), lit by the sun / lamps only — the old emissive glint glowed at night and in shadow
  if (flakes) {
    const ng = normalGeometry;
    const ax = abs(ng.x), ay = abs(ng.y), az = abs(ng.z);
    const coord = select(ax.greaterThan(max(ay, az)), pg.yz, select(ay.greaterThan(az), pg.xz, pg.xy));
    applyFlakeGlints(m, { coord, position: pg }, SLATE_FLAKES);
    materialModelStats.flakeRocks++;
  }
  return m;
}
